import request from "@/utils/request";

/**
 * 获取OSS上传签名URL和任务ID
 * @param {object} payload - 包含图像文件名列表的对象, 例如 { images: ["image1.jpg", "image2.png"] }
 * @returns {Promise} - 返回一个包含 task_id 和上传URL列表的 Promise 对象
 */
export function uploadImagesAndGetTask(payload) {
  return request({
    url: "/api/v1/riqa/upload",
    method: "post",
    data: payload,
  });
}

/**
 * 根据任务ID查询图像处理任务的进度
 * @param {string} taskId - 任务的唯一标识符 (UUID)
 * @returns {Promise} - 返回一个包含进度百分比的 Promise 对象
 */
export function getTaskProgress(taskId) {
  return request({
    url: `/api/v1/riqa/tasks/${taskId}/progress`,
    method: "get",
    showLoading: false,
  });
}

/**
 * 根据任务ID获取图像处理任务的最终结果
 * @param {string} taskId - 任务的唯一标识符 (UUID)
 * @returns {Promise} - 返回一个包含完整分析结果的 Promise 对象
 */
export function getTaskResult(taskId) {
  return request({
    url: `/api/v1/riqa/tasks/${taskId}/result`,
    method: "get",
  });
}

/**
 * 根据 ID 删除指定的图像
 * @param {number | string} imageId - 要删除的图像的 ID
 * @returns {Promise}
 */
export function deleteImage(imageId) {
  return request({
    url: `/api/v1/riqa/images/${imageId}`,
    method: "delete",
  });
}

/**
 * 更新指定分析类型的用户配置
 * @param {object} payload - 包含分析类型、配置ID和配置数据的对象
 * @returns {Promise}
 */
export function updateAnalysisSettings(payload) {
  return request({
    url: "/api/v1/riqa/analysis-settings/update_settings",
    method: "put",
    data: payload,
  });
}

/**
 * 根据分析类型获取默认的分析设置
 * @param {string} analysisType - 分析类型 (例如 'Color Reproduction')
 * @returns {Promise} - 返回一个包含默认配置的 Promise 对象
 */
export function getDefaultAnalysisSettings(analysisType) {
  return request({
    url: "/api/v1/riqa/analysis-settings/defaults",
    method: "get",
    params: { analysis_type: analysisType },
  });
}

/**
 * 对指定图像提交分析任务
 * @param {number | string} imageId - 要分析的图像的 ID
 * @param {object} params - 包含分析类型和设置的参数对象
 * @returns {Promise}
 */
export function startAnalysis(imageId, params) {
  return request({
    url: `/api/v1/riqa/analyze/${imageId}`,
    method: "post",
    data: params,
  });
}

/**
 * 获取当前用户的分析结果列表 (历史记录)
 * @param {number[]} [imageIds=null] - 可选的图像ID数组。如果提供，则只查询这些ID的结果。
 * @returns {Promise}
 */
export function getAnalysisHistory(imageIds = null) {
  const config = {
    url: "/api/v1/riqa/analyses",
    method: "get",
    params: {}, // 初始化 params 对象
    showLoading: false,
  };

  // 如果传入了有效的ID数组，则将其添加到请求参数中
  // Axios 会自动将 { image_id: [1, 2, 3] } 序列化为 ?image_id=1&image_id=2&image_id=3
  if (imageIds && imageIds.length > 0) {
    config.params.image_id = imageIds;
  }
  console.log("Fetching analysis history with config:", config);

  return request(config);
}

/**
 * 根据图像ID获取单个分析报告的详细内容
 * @param {number | string} imageId - 图像的 ID
 * @returns {Promise} - 返回包含结构化报告数据的 Promise
 */
export function getAnalysisDetail(imageId) {
  return request({
    // 确认接口地址
    url: `/api/v1/riqa/analysis-results/${imageId}`,
    method: "get",
    showLoading: false,
  });
}

/**
 * 请求下载分析报告
 * @param {object} data - 请求体, e.g., { type: "csv,pdf", image_id: "1,2" }
 * @returns {Promise} - 返回包含文件 blob 数据的 Promise
 */
export function downloadReport(data) {
  return request({
    url: "/api/v1/riqa/download-report",
    method: "post",
    data, // data 会被 axios 序列化为 JSON
    responseType: "blob", // 告诉 axios 我们期望的响应体是二进制 Blob 对象
  });
}
